import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BundleView, TimeOfUseDirection } from '@totalenergiescode/springbox-totalenergies-ui-library/bundles';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Languages } from 'src/app/route-translate.strategy';
import { environment } from '../../../environments/environment';
import { Localities, LocalityApi } from '../api/locality.api';
import { OlregApiService } from '../api/olreg.api';
import { ProductCatalogApi, PromoDetailsResult } from '../api/product-catalog.api';
import { SimulationEngineResponse, SimulatorEngineApi, SimulatorEngineRequest } from '../api/simulator-engine.api';
import { TrackingApi } from '../api/tracking.api';
import { BundleDetails, Bundles, ProductCatalogTimeOfUseConfiguration } from '../bundle/bundle';
import { navigateAndThrow } from '../service/error-util';
import { InjectionService } from '../service/injection.service';
import { QuestionOrderHandlerService } from '../service/question-order-handler.service';
import { ClientType } from '../service/type-detector.service';
import { ZipCodeService } from '../service/zip-code.service';
import {
  Consumptions,
  Energy,
  findCurrentSite,
  INITIAL_STATE,
  MeterType,
  NumberOfSite,
  Site,
  State,
} from '../state/state';
import { DeprecatedOlregMapperService } from './mapper/deprecated-olreg-mapper.service';
import { OlregB2bMapperService } from './mapper/olreg-b2b-mapper.service';
import { OlregB2CMapperService } from './mapper/olreg-b2c-mapper.service';
import { QueryParamsMapperService } from './mapper/query-params-mapper.service';
import { SimulatorEngineMapperService } from './mapper/simulator-engine-mapper.service';
import { TrackingMapperService } from './mapper/tracking-mapper.service';

@Injectable({ providedIn: 'root' })
export class QuestionFacade {
  private readonly questionOrderHandlerService = inject(QuestionOrderHandlerService);
  private readonly olregApi = inject(OlregApiService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly localityApi = inject(LocalityApi);
  private readonly router = inject(Router);
  private readonly trackingApi = inject(TrackingApi);
  private readonly translateService = inject(TranslateService);
  private readonly productCatalogApi = inject(ProductCatalogApi);
  private readonly simulatorEngineApi = inject(SimulatorEngineApi);
  private readonly cookieService = inject(CookieService);

  readonly state$ = new BehaviorSubject<State>(INITIAL_STATE);
  readonly sites$ = this.state$.asObservable().pipe(
    map((state) => state.sites),
    distinctUntilChanged(),
  );
  readonly currentStep$ = this.state$.asObservable().pipe(
    map((state) => state.currentStepUrl),
    distinctUntilChanged(),
  );
  readonly simulations$ = this.state$.asObservable().pipe(
    filter((state) => (state.sites || []).length > 0),
    map((state) => state.sites[0]),
    filter((firstSite) => !!firstSite.simulation),
    distinctUntilChanged(),
  );

  private readonly brusselSolarPanelKva = 5;

  constructor() {
    this.handlePromotion();
    this.handleTrackingServiceEvent();
  }

  redirectFromWebsiteToOlreg(bundleCode: string): void {
    const sites = this.state$.value.sites;
    if (sites[0]?.simulation) {
      this.goToOlreg(bundleCode);
    }
  }

  isB2C(): boolean {
    return this.state$.value.type === ClientType.B2C;
  }

  trackAndGoToOlreg(bundleCode: string, price: number): void {
    this.sendSimulatorInteractionProductTrackingData(bundleCode, price / 12);
    // setTimeout to ensure the tracking data is updated before redirect to olreg
    setTimeout(() => {
      this.goToOlreg(bundleCode);
    });
  }

  goToOlreg(bundleCode: string): void {
    if (this.state$.value.type === ClientType.B2B) {
      this.goToOlregB2B(bundleCode);
    } else {
      this.gotToOlregB2C(bundleCode);
    }
  }

  next(): void {
    const state = this.state$.value;

    const nextUrl = this.questionOrderHandlerService.getNextQuestionUrl(state);
    const remainingStep = this.questionOrderHandlerService.remainingSteps({ ...state, currentStepUrl: nextUrl });
    const siteUpdated = this.updateStepsReached(state);
    const updatedNumberOfStepExecuted = state.numberOfStepExecuted + 1;
    this.setState((currentState) => ({
      ...currentState,
      currentStepUrl: nextUrl,
      sites: [...state.sites].map((site) => (site === this.getCurrentSite() ? siteUpdated : site)),
      numberOfStep: state.numberOfStepExecuted + remainingStep,
      numberOfStepExecuted: updatedNumberOfStepExecuted,
    }));

    this.navigateToStep(nextUrl, updatedNumberOfStepExecuted);
  }

  previous(): void {
    const state = this.state$.value;
    const previousUrl = this.questionOrderHandlerService.getPreviousQuestionUrl(state);
    const remainingStep = this.questionOrderHandlerService.remainingSteps({ ...state, currentStepUrl: previousUrl });
    const updatedNumberOfStepExecuted = state.numberOfStepExecuted - 1;
    this.setState((currentState) => ({
      ...currentState,
      currentStepUrl: previousUrl,
      numberOfStep: remainingStep + updatedNumberOfStepExecuted,
      numberOfStepExecuted: updatedNumberOfStepExecuted,
    }));

    this.navigateToStep(previousUrl, updatedNumberOfStepExecuted);
  }

  private navigateToStep(url: string, numberOfStepExecuted: number): void {
    const queryParams = QueryParamsMapperService.getQueryParamsObject();
    if (queryParams.energyChosen) {
      queryParams.energyChosen = findCurrentSite(this.state$.value).energy ?? queryParams.energyChosen;
    }
    this.router.navigate([url], {
      queryParamsHandling: 'merge',
      queryParams,
      state: { step: numberOfStepExecuted },
    });
  }

  updateSiteData(data: Partial<Site>): void {
    const siteUpdated = { ...findCurrentSite(this.state$.value), ...data };
    this.setState((state) => ({
      ...state,
      sites: [...state.sites].map((site) => (site === findCurrentSite(this.state$.value) ? siteUpdated : site)),
    }));
  }

  updateConsumption(consumptions: Partial<Consumptions>, consumptionHelp: string): void {
    const currentSite = findCurrentSite(this.state$.value);
    this.updateSiteData({
      consumptionHelp,
      consumptions: {
        consumptionHigh: currentSite.meterType === MeterType.MONO ? null : consumptions.consumptionHigh,
        consumptionLow: currentSite.meterType === MeterType.MONO ? null : consumptions.consumptionLow,
        consumptionMono: currentSite.meterType === MeterType.MONO ? consumptions.consumptionMono : null,
        consumptionExclusiveNight: currentSite.exclusiveNight ? consumptions.consumptionExclusiveNight : null,
        gasConsumption:
          currentSite.energy === Energy.GAS || currentSite.energy === Energy.ELEC_GAS
            ? consumptions.gasConsumption
            : null,
      } as Consumptions,
    });
  }

  reset(): void {
    this.state$.next(INITIAL_STATE);
    this.router.navigate([this.state$.value.currentStepUrl], {
      queryParamsHandling: 'merge',
      queryParams: QueryParamsMapperService.getQueryParamsObject(),
      state: { step: this.state$.value.numberOfStepExecuted },
    });
  }

  updateData(data: Partial<State>): void {
    this.setState((state) => ({ ...state, ...data }));
  }

  updateStepsReached(state: State): Site {
    const currentSite = this.getCurrentSite();
    currentSite.stepsReached.add(state.currentStepUrl);
    return currentSite;
  }

  addSite(): void {
    const stateUpdated = {
      ...this.state$.value,
      currentSiteIndex: this.state$.value.sites.length,
      currentStepUrl: INITIAL_STATE.currentStepUrl,
      sites: [...this.state$.value.sites, { ...INITIAL_STATE.sites[0], stepsReached: new Set<string>() }],
    };
    const stateCurrentStepUpdated = {
      ...stateUpdated,
      currentStepUrl: this.questionOrderHandlerService.getNextQuestionUrl(stateUpdated),
    };
    this.updateData(stateCurrentStepUpdated);
    this.navigateToStep(stateCurrentStepUpdated.currentStepUrl, 0);
    this.sendSimulatorInteractionTrackingData('Add delivery point');
  }

  deleteSite(siteIndex: number): void {
    const sites = [...this.state$.value.sites].filter((currentSite, index) => index !== siteIndex);
    this.updateData({ sites: sites });
    this.sendSimulatorInteractionTrackingData('Remove delivery point');
  }

  saveSiteName(siteIndex: number, name: string): void {
    this.updateData({ currentSiteIndex: siteIndex });
    this.updateSiteData({ siteName: name });
  }

  findSiteData<T>(mapFn: (data: Site) => T): Observable<T> {
    return this.sites$.pipe(
      map(() => mapFn(findCurrentSite(this.state$.value))),
      distinctUntilChanged(),
    );
  }

  findData<T>(mapFn: (state: State) => T): Observable<T> {
    return this.state$.pipe(
      map((state) => mapFn(state)),
      distinctUntilChanged(),
    );
  }

  isLastQuestion(): boolean {
    return this.questionOrderHandlerService.isLast(this.state$.value);
  }

  isFirstQuestion(): boolean {
    return this.questionOrderHandlerService.isFirst(this.state$.value);
  }

  getLocalities(zipCode: number): Observable<Localities> {
    return this.localityApi.get(zipCode);
  }

  hasInjectionFields(): boolean {
    const currentSite = this.getCurrentSite();
    return InjectionService.hasInjectionFields(currentSite);
  }

  isElecOrDual(): [isElec: boolean, isDual: boolean] {
    const { hasSolarPanels = false, smartMeter = false, energy, zipCode = 0 } = this.getCurrentSite();

    const isElec: boolean =
      energy != null &&
      energy.includes(Energy.ELEC) &&
      hasSolarPanels &&
      ((ZipCodeService.isFlanders(zipCode) && smartMeter) || ZipCodeService.isBrussels(zipCode));
    const isDual = isElec && energy.includes(Energy.ELEC_GAS);
    return [isElec, isDual];
  }

  getDefaultInjectionValues(): number[] {
    const currentSite = this.getCurrentSite();
    return InjectionService.getDefaultInjectionValues(currentSite);
  }

  onErrorPageReached(): void {
    const trackingData = TrackingMapperService.toErrorDisplayTrackingData(
      this.state$.value,
      this.translateService.currentLang as Languages,
      this.state$.value.type === ClientType.B2B,
    );
    this.trackingApi.sendDisplayEvent(trackingData);
  }

  onPhoneClicked(): void {
    const trackingData = TrackingMapperService.toContactInteractionTrackingData('Phone');
    this.trackingApi.sendSimulatorInteractionEvent(trackingData);
  }

  onEmailClicked(): void {
    const trackingData = TrackingMapperService.toContactInteractionTrackingData('Email');
    this.trackingApi.sendSimulatorInteractionEvent(trackingData);
  }

  isProsumerInWalloniaOrFlanders(): Observable<boolean> {
    return this.sites$.pipe(
      map((sites) =>
        sites.some(
          (site) => site.hasSolarPanels && site.solarPanelKva <= 10 && !ZipCodeService.isBrussels(site.zipCode),
        ),
      ),
    );
  }

  isProsumerInBrussels(): Observable<boolean> {
    return this.sites$.pipe(
      map((sites) =>
        sites.some(
          (site) =>
            site.hasSolarPanels &&
            site.solarPanelKva <= this.brusselSolarPanelKva &&
            ZipCodeService.isBrussels(site.zipCode),
        ),
      ),
    );
  }

  isBrussels(): Observable<boolean> {
    return this.sites$.pipe(map((sites) => sites.some((site) => ZipCodeService.isBrussels(site.zipCode))));
  }

  createSimulationWithPayload(payload: SimulatorEngineRequest[]): Observable<SimulationEngineResponse> {
    return this.simulatorEngineApi.post(payload).pipe(
      tap((simulation: SimulationEngineResponse) => {
        this.hydrateStateWithSimulation(simulation);
        this.setUpCookiesForExistingSimulation(this.state$.value);
      }),
      take(1),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  fetchSimulationById(simulationId: string): Observable<SimulationEngineResponse> {
    return this.simulatorEngineApi.get(simulationId).pipe(
      switchMap((response: SimulationEngineResponse) => {
        if (!response?.simulationId || response?.simulations.length === 0) {
          // Already created simulations are accesible only for some time, after that API returns 200 with empty body
          return throwError(() => new Error('Simulation not found in response'));
        }
        return of(response);
      }),
      tap((simulation: SimulationEngineResponse) => this.hydrateStateWithSimulation(simulation)),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
      take(1),
    );
  }

  createSimulationFromState(): Observable<SimulationEngineResponse> {
    if (this.state$.value.simulationId || this.isInitialData()) {
      return of(void 0);
    }

    return this.createSimulationWithPayload(this.createSimulationPayload(this.state$.value.sites));
  }

  // Time of use simulation is only for B2C, so we will always have only one site
  updateSimulationWithTimeOfUse(
    timeOfUseConsumptions: Pick<Consumptions, 'consumptionTI1' | 'consumptionTI2' | 'consumptionTI3'>,
  ): Observable<SimulationEngineResponse> {
    const { currentSiteIndex, simulationId, sites } = this.state$.value;
    const currentSite = sites[currentSiteIndex];

    const updatedSite: Site = {
      ...currentSite,
      consumptions: {
        ...currentSite.consumptions,
        ...timeOfUseConsumptions,
      },
      knowConsumption: true, // When we do update then we already know the consumption for this site
    };

    return this.simulatorEngineApi.put(simulationId, this.createSimulationPayload([updatedSite])).pipe(
      tap((simulation: SimulationEngineResponse) => this.hydrateStateWithSimulation(simulation)),
      take(1),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
    );
  }

  private createSimulationPayload(sites: Site[]): SimulatorEngineRequest[] {
    const { type } = this.state$.value;
    const { promoCode, includeDynamic } = QueryParamsMapperService.getQueryParamsObject();

    return sites.map((site) =>
      SimulatorEngineMapperService.toSimulatorEngineRequest(
        site,
        type,
        promoCode,
        includeDynamic === 'true' || environment.includeDynamicByDefault,
      ),
    );
  }

  // We set up cookies to be able to retrieve existing simulation from drupal website
  private setUpCookiesForExistingSimulation(state: State): void {
    this.cookieService.delete(`totalenergies_simulator_${state.type.toLowerCase()}`, '/');
    // Temporarily disable the B2C simulator cookie in production for the Website project (Drupal)
    if (environment.production && state.type === ClientType.B2C) {
      return;
    }
    const now = new Date();
    // Last day of current month at 23:59:59.999 (equivalent to dayjs().endOf('month'))
    const endOfMonthBoundary = {
      lastHour: 23,
      lastMinute: 59,
      lastSecond: 59,
      lastMillisecond: 999,
      // using day = 0 returns the last day of the previous month, so month+1 with day 0 gives end of current month
      lastDayOfPreviousMonth: 0,
    };

    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      endOfMonthBoundary.lastDayOfPreviousMonth,
      endOfMonthBoundary.lastHour,
      endOfMonthBoundary.lastMinute,
      endOfMonthBoundary.lastSecond,
      endOfMonthBoundary.lastMillisecond,
    );
    const firstSite = state.sites[0];
    const simulatorCookies = {
      simulationId: state.simulationId,
      hasSolarPanels: firstSite.hasSolarPanels,
      electricVehicle: firstSite.hasElectricVehicle ? 1 : 0,
      assistancePrice: '',
    };
    this.cookieService.set(
      `totalenergies_simulator_${state.type.toLowerCase()}`,
      JSON.stringify(simulatorCookies),
      endOfMonth,
      '/',
    );
  }

  getBundlesDetails(): Observable<BundleDetails[]> {
    return this.getBundles().pipe(
      first(),
      map((codes) => this.getBundleCodes(codes)),
      switchMap((codes) => (codes.length > 0 ? this.productCatalogApi.getBundlesDetails(codes) : of([]))),
    );
  }

  getBundles(): Observable<Bundles[]> {
    return this.findData((state) => state.sites.map((site) => site.simulation)).pipe(
      filter(() => !this.isInitialData()),
      map((codes: Bundles[][]) => codes?.flat() || []),
      map((bundles) => bundles.filter((bundle) => !!bundle)),
      filter((bundles) => !!bundles && bundles.length > 0),
    );
  }

  getPromotionDetailsWithSource(promoCode: string, showLoader = true): Observable<PromoDetailsResult> {
    return this.productCatalogApi.getPromoDetailsWithSource(promoCode, showLoader);
  }

  sendSimulatorInteractionTrackingData(trackingEventValue: string): void {
    const trackingData = TrackingMapperService.toSimulatorInteractionTrackingData(trackingEventValue);
    this.trackingApi.sendSimulatorInteractionEvent(trackingData);
  }

  sendSimulatorInteractionProductTrackingData(bundleCode: string, price: number): void {
    const trackingData = TrackingMapperService.toSimulatorInteractionProductTrackingData(bundleCode, price);
    this.trackingApi.sendSimulatorInteractionEvent(trackingData);
  }

  getCurrentSite(): Site {
    return findCurrentSite(this.state$.value);
  }

  sortBundleDetailsByWeighting<T extends BundleDetails | BundleView>(bundles: T[], descending = true): T[] {
    return bundles.sort((firstBundle: T, secondBundle: T) =>
      (!descending && firstBundle.weighting > secondBundle.weighting) ||
      (descending && firstBundle.weighting < secondBundle.weighting)
        ? 1
        : -1,
    );
  }

  private splitLastOccurrence(str: string, substring: string): string[] {
    const lastIndex = str.lastIndexOf(substring);
    const before = str.slice(0, lastIndex);
    const after = str.slice(lastIndex + 1);
    return [before, after];
  }

  private isInitialData(): boolean {
    return JSON.stringify({ ...this.state$.value, promo: undefined }) === JSON.stringify(INITIAL_STATE);
  }

  /**
   * should be extracted into a service and tested
   * @param bundles
   * @private
   */
  private getBundleCodes(bundles?: Bundles[]): string[] {
    if (!bundles || bundles.length === 0 || bundles[0] === undefined) {
      return [];
    }

    const codeSet = new Set<string>();
    bundles.forEach((bundles: Bundles) => codeSet.add(bundles.code));
    if (codeSet.size === 1) {
      return Array.from(codeSet);
    }

    const splitedCodeArray = Array.from(codeSet).map((code: string) => this.splitLastOccurrence(code, '_'));

    const codeArray = splitedCodeArray.map(([product, energy]: string[]) => {
      if (energy.toUpperCase() === 'DUAL') {
        return `${product}_${energy}`;
      }
      if (splitedCodeArray.find((code: string[]) => code[0] === product && code[1].toUpperCase() === 'DUAL')) {
        return `${product}_DUAL`;
      }
      if (
        splitedCodeArray.find((code: string[]) => code[0] === product && code[1].toUpperCase() !== energy.toUpperCase())
      ) {
        return `${product}_DUAL`;
      }
      return `${product}_${energy}`;
    });

    return Array.from(new Set(codeArray)); // return & remove duplicates
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    return navigateAndThrow(this.router, 'Error !', error);
  }

  private handleTrackingServiceEvent(): void {
    this.currentStep$
      .pipe(
        switchMap((step) => {
          return step === '/result' ? this.simulations$.pipe(first(), mapTo(true)) : of(true);
        }),
      )
      .subscribe(() => this.sendDisplayTrackingData());

    this.translateService.onLangChange
      .pipe(
        skip(1),
        map((langEvent) => langEvent.lang),
      )
      .subscribe((lang) => {
        const simulatorInteractionTrackingData = TrackingMapperService.toNavigationInteractionTrackingData(lang);
        this.trackingApi.sendSimulatorInteractionEvent(simulatorInteractionTrackingData);
      });
  }

  private sendDisplayTrackingData(): void {
    const trackingStepName = this.questionOrderHandlerService.getCurrentStepDisplayTrackingValue(this.state$.value);
    const trackingData =
      this.state$.value.type === ClientType.B2C
        ? TrackingMapperService.toDisplayTrackingDataB2c(
            this.state$.value,
            this.translateService.currentLang as Languages,
            trackingStepName,
          )
        : TrackingMapperService.toDisplayTrackingDataB2b(
            this.state$.value,
            this.translateService.currentLang as Languages,
            trackingStepName,
          );
    this.trackingApi.sendDisplayEvent(trackingData);
  }

  private setState(updateFn: (currentState: State) => State): void {
    const newState = updateFn(this.state$.value);
    this.state$.next(newState);
  }

  private goToOlregB2B(bundleCode: string): void {
    const sites = this.state$.value.sites;
    let urlSearchParams = OlregB2bMapperService.toOlregQueryParams(
      bundleCode,
      sites,
      this.state$.value.promo,
      this.state$.value.simulationId,
    );
    urlSearchParams = DeprecatedOlregMapperService.toLegacyOlRegQueryParams(urlSearchParams); // to be removed once PC integrated
    this.olregApi.redirectB2B(this.translateService.currentLang, urlSearchParams);
  }

  private gotToOlregB2C(bundleCode: string): void {
    const sites = this.state$.value.sites;
    this.activatedRoute.queryParams.pipe(first()).subscribe((params) => {
      const urlSearchParams = OlregB2CMapperService.toOlregQueryParams(
        bundleCode,
        sites[0],
        params['promoCode'],
        params['sponsorReference'],
        params['isGeep'],
        params['agentUsername'],
        params['includeDynamic'] || environment.includeDynamicByDefault,
        this.state$.value.simulationId,
      );
      this.olregApi.redirectB2C(this.translateService.currentLang, urlSearchParams);
    });
  }

  hydrateStateWithSimulation(simulationResponse: SimulationEngineResponse): void {
    this.setState((state) => ({
      ...state,
      currentStepUrl: this.questionOrderHandlerService.getLastStep(state),
      numberOfSites: NumberOfSite.LESSER_OR_EQUAL_THAN_8,
      simulationId: simulationResponse.simulationId,
      productCatalogTimeOfUseConfiguration:
        simulationResponse?.productCatalogTimeOfUseConfiguration?.filter(
          (config) => config.direction === TimeOfUseDirection.CONSUMPTION,
        ) ?? null,
      sites: simulationResponse.simulations.map((sim) =>
        SimulatorEngineMapperService.toSiteState(sim.simulationRequest, sim.bundles),
      ),
    }));
  }

  getProductCatalogTimeOfUseConfiguration(): ProductCatalogTimeOfUseConfiguration[] {
    return this.state$.value.productCatalogTimeOfUseConfiguration ?? [];
  }

  private handlePromotion(): void {
    this.activatedRoute.queryParams
      .pipe(
        filter((params) => !!params),
        filter((params) => !!params['promoCode']),
        map((params) => params['promoCode']),
        first(),
      )
      .subscribe((promoCode) => {
        this.setState((state) => ({ ...state, promo: promoCode }));
      });
  }
}
